import React, { useState, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { useWordle } from './hooks/useWordle';
import { GameGrid } from './components/game/GameGrid';
import { Keyboard } from './components/game/Keyboard';
import { HelpModal } from './components/game/HelpModal';
import { StatsModal } from './components/game/StatsModal';
import { BalloonCelebration } from './components/game/BalloonCelebration';
import { ChallengeModal } from './components/game/ChallengeModal';
import { LeaderboardModal } from './components/game/LeaderboardModal';
import { Button } from './components/ui/button';
import { HelpCircle, BarChart3, Users, X, RefreshCw, Share2, UserPlus, Trophy } from 'lucide-react';

const GamePage = () => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  
  const game = useWordle();
  
  const handleKeyPress = useCallback((key) => {
    if (key === 'ENTER') {
      game.submitGuess();
    } else if (key === 'BACK') {
      game.removeLetter();
    } else {
      game.addLetter(key);
    }
  }, [game]);
  
  const handleShare = useCallback(async () => {
    const text = game.getShareText();
    
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.success('Results copied to clipboard');
      }
    } catch (err) {
      toast.error('Could not share results');
    }
  }, [game]);
  
  const handleReset = useCallback(() => {
    game.resetGame();
    setStatsOpen(false);
    toast('Game reset');
  }, [game]);
  
  const isPlaying = game.mode === 'playing';

  return (
    <div className="min-h-screen bg-[#FFF4F2] flex flex-col" data-testid="game-container">
      {/* Challenge Mode Banner */}
      {game.isChallenge && (
        <div className="bg-[#6aaa64] text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Challenge Mode</span>
          </div>
          <button 
            onClick={game.exitChallenge}
            className="flex items-center gap-1 text-sm hover:opacity-80"
          >
            <X className="w-4 h-4" />
            Exit
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="flex items-center justify-between w-full border-b border-[#d3d6da] px-4 py-3">
        <div className="flex items-center gap-3 w-[80px]">
          {/* Challenge Button */}
          {!game.isChallenge && (
            <button 
              onClick={() => setChallengeOpen(true)}
              className="text-black hover:text-gray-600"
              data-testid="challenge-button"
              aria-label="Challenge a friend"
            >
              <Users className="w-6 h-6" />
            </button>
          )}
          {/* Leaderboard Button */}
          <button 
            onClick={() => setLeaderboardOpen(true)}
            className="text-yellow-500 hover:text-yellow-600"
            data-testid="leaderboard-button"
            aria-label="Challenge leaderboard"
          >
            <Trophy className="w-6 h-6" />
          </button>
        </div>
        
        <h1 className="font-serif text-[32px] font-bold tracking-tight text-black" data-testid="game-title">
          Five Loop
        </h1>
        
        <div className="flex items-center gap-3 w-[80px] justify-end">
          <button 
            onClick={() => setHelpOpen(true)}
            className="text-black hover:text-gray-600"
            data-testid="help-button"
            aria-label="How to play"
          >
            <HelpCircle className="w-6 h-6" />
          </button>
          <button 
            onClick={() => setStatsOpen(true)}
            className="text-black hover:text-gray-600"
            data-testid="stats-button"
            aria-label="Statistics"
          >
            <BarChart3 className="w-6 h-6" />
          </button>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4">
        {/* Game Grid */}
        <div className="py-6">
          <GameGrid 
            board={game.board}
            currentGuess={game.currentGuess}
            shake={game.shake}
            wordLength={game.wordLength}
            maxGuesses={game.maxGuesses}
          />
        </div>
        
        {/* Challenge End Actions - Visible directly on screen */}
        {game.isChallenge && (game.mode === 'chain_ended' || game.mode === 'daily_complete') && (
          <div className="flex flex-col items-center gap-3 mt-2 mb-4">
            {/* Score display */}
            {game.challengeTimeTaken && (
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-1">
                <span className="flex items-center gap-1">
                  <span className="font-medium">{game.guessesUsedThisRound}/6</span> guesses
                </span>
                <span className="flex items-center gap-1">
                  <span className="font-medium">{Math.floor(game.challengeTimeTaken / 60)}:{String(game.challengeTimeTaken % 60).padStart(2, '0')}</span> time
                </span>
              </div>
            )}
            
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                className="bg-white border-gray-300 hover:bg-gray-100 text-black rounded px-4"
                onClick={game.replayChallenge}
                data-testid="inline-play-again-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button
                className="bg-[#6aaa64] hover:bg-[#5a9a54] text-white rounded px-4"
                onClick={handleShare}
                data-testid="inline-share-button"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                className="bg-white border-gray-300 hover:bg-gray-100 text-black rounded px-4"
                onClick={() => setChallengeOpen(true)}
                data-testid="inline-new-challenge-button"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                New Challenge
              </Button>
              <Button
                variant="outline"
                className="bg-yellow-50 border-yellow-300 hover:bg-yellow-100 text-yellow-700 rounded px-4"
                onClick={() => setLeaderboardOpen(true)}
                data-testid="inline-leaderboard-button"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Leaderboard
              </Button>
            </div>
          </div>
        )}
      </main>
      
      {/* Keyboard */}
      <div className="pb-4 pt-2">
        <Keyboard 
          letterStates={game.letterStates}
          onKeyPress={handleKeyPress}
          disabled={!isPlaying}
        />
      </div>
      
      {/* Modals */}
      <HelpModal open={helpOpen} onOpenChange={setHelpOpen} />
      <StatsModal 
        open={statsOpen} 
        onOpenChange={setStatsOpen}
        streak={game.streak}
        totalDailyWins={game.totalDailyWins}
        roundsCompleted={game.roundsCompleted}
        maxRounds={game.maxRounds}
        countdown={game.countdown}
        mode={game.mode}
        history={game.history}
        onShare={handleShare}
        onReset={handleReset}
        isChallenge={game.isChallenge}
        onReplayChallenge={game.replayChallenge}
      />
      <ChallengeModal open={challengeOpen} onOpenChange={setChallengeOpen} />
      <LeaderboardModal 
        open={leaderboardOpen} 
        onOpenChange={setLeaderboardOpen}
        getChallengeStats={game.getChallengeStats}
      />
      
      {/* Toast */}
      <Toaster 
        position="top-center" 
        toastOptions={{
          className: 'bg-black text-white rounded-lg',
        }}
      />
      
      {/* Balloon Celebration */}
      <BalloonCelebration 
        show={game.showCelebration} 
        onComplete={game.clearCelebration}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<GamePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
